import { Form, Formik } from "formik";
import { type GatewayAssetOut, gatewaySettingsTypeEnum } from "kubb";
import { toInteger } from "lodash-es";
import type React from "react";
import { useMemo, useState } from "react";
import AssetExpectedFrequencySwitch from "../../../../components/Asset/AssetExpectedFrequencySwitch";
import AssetGatewaySettingTypeSwitch from "../../../../components/Asset/AssetGatewaySettingTypeSwtich";
import AlertBanner from "../../../../components/Banner/AlertBanner";
import { ErrorBanner } from "../../../../components/Error/ErrorBanner";
import { ButtonGroupField } from "../../../../components/Forms/ButtonGroup";
import KeyValueMapField from "../../../../components/Forms/KeyValueMapField/KeyValueMapField";
import { Button } from "../../../../components/Theme/button";
import { useUpdateAsset } from "../../../../hooks/updateEntity.tsx";

interface Props {
  gateway: GatewayAssetOut;
  refresh?: () => any;
}

export default function GatewaySettingForm(props: Props): React.ReactElement {
  const [state, setState] = useState<"error" | "completed" | undefined>(
    undefined,
  );

  const { update, error } = useUpdateAsset(props.gateway);

  const isElvaco = props.gateway.manufacturer === "elvaco";

  // Transform custom_configuration from object to array format for KeyValueMapField
  // Memoized to prevent regenerating UUIDs on every render
  const customConfigArray = useMemo(
    () =>
      props.gateway.configuration.custom_configuration
        ? Object.entries(props.gateway.configuration.custom_configuration).map(
            ([key, value]) => ({
              key,
              value: String(value),
              id: crypto.randomUUID(),
            }),
          )
        : [],
    [props.gateway.configuration.custom_configuration],
  );

  const initialValues = {
    assetExpectedFrequency: props.gateway.configuration.collection_frequency
      ? props.gateway.configuration.collection_frequency.toString()
      : "15",
    assetSettingType:
      props.gateway.configuration.manufacturer_specific_settings
        ?.mbus_settings ||
      (isElvaco ? gatewaySettingsTypeEnum.ELVACO_DEFAULT : undefined),
    assetPostFrequency: props.gateway.configuration.post_frequency
      ? props.gateway.configuration.post_frequency.toString()
      : "Default",
    postRawData:
      props.gateway.configuration.collect_raw_data ||
      (isElvaco ? false : undefined),
    autoSync: props.gateway.configuration.auto_sync || false,
    autoSyncDevices: props.gateway.configuration.sync_devices || false,
    customConfiguration: customConfigArray,
  };
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={(values, { setSubmitting }): void => {
        setState(undefined);

        // Transform customConfiguration from array back to object format
        const customConfigObject =
          values.customConfiguration && values.customConfiguration.length > 0
            ? Object.fromEntries(
                values.customConfiguration.map((pair) => [
                  pair.key,
                  pair.value,
                ]),
              )
            : undefined;

        update({
          ...props.gateway,
          configuration: {
            ...props.gateway.configuration,
            auto_sync: values.autoSync,
            sync_devices: values.autoSyncDevices,
            collection_frequency: toInteger(values.assetExpectedFrequency),
            manufacturer_specific_settings: isElvaco
              ? {
                  mbus_settings: values.assetSettingType,
                }
              : undefined,
            post_frequency:
              values.assetPostFrequency === "Default"
                ? undefined
                : toInteger(values.assetPostFrequency),
            collect_raw_data: isElvaco ? values.postRawData : undefined,
            custom_configuration: customConfigObject,
          },
        })
          .then(() => {
            props.refresh?.();
            setState("completed");
          })
          .finally(() => setSubmitting(false));
      }}
    >
      {({ isSubmitting, isValid }): React.ReactElement => (
        <Form>
          {isElvaco && (
            <AssetGatewaySettingTypeSwitch fieldName={"assetSettingType"} />
          )}
          <AssetExpectedFrequencySwitch
            fieldName={"assetExpectedFrequency"}
            title={"Data Collection Frequency"}
            abbreviate={true}
          />
          <AssetExpectedFrequencySwitch
            fieldName={"assetPostFrequency"}
            title={"Data Post Frequency"}
            abbreviate={true}
            include1Minute={false}
            includeDeviceNormal={true}
          />
          {isElvaco && (
            <ButtonGroupField
              fieldName={"postRawData"}
              label={{ label: "Post Raw MBus" }}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
            />
          )}
          <ButtonGroupField
            fieldName={"autoSync"}
            label={{ label: "Automatically Sync Configuration" }}
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
          <ButtonGroupField
            fieldName={"autoSyncDevices"}
            label={{ label: "Automatically Sync Device List" }}
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
          <KeyValueMapField
            fieldName="customConfiguration"
            label="Custom Configuration"
          />

          <div className={"py-4 text-right"}>
            {(state || error) && (
              <div>
                {state === "completed" && (
                  <AlertBanner className={"bg-success"}>
                    Settings successfully updated.
                  </AlertBanner>
                )}
                {state === "error" && (
                  <AlertBanner className={"bg-danger"}>
                    Error updating settings.{" "}
                    {error ? JSON.stringify(error) : "Unknown"}
                  </AlertBanner>
                )}
                <ErrorBanner error={error} />
              </div>
            )}
            <Button
              type={"submit"}
              color={"brandLight"}
              disabled={isSubmitting || !isValid}
            >
              Update
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
